import { WhiteBox } from '../../../../../baseComponents/AppLayout/WhiteBox'
import { ComponentWithPermissions } from '../../../../../baseComponents/CreateRoutering/types'
import { BigTitleAndRightLayout } from '../../../../../baseComponents/Layout/TitleAndRightLayout'
import LoaderDecider from '../../../../../baseComponents/LoaderDecider'
import { ButtonConfigColumnsWithModal } from '../../../../../baseComponents/ModalsLayouts/ModalConfigColumnsTabs'
import { TableConfiguration } from '../../../../../baseComponents/TwinTable/types'
import useConfigColumns, { SelectedColumnsAndFilters } from '../../../../../utils/hooks/useConfigColumns'
import { InputTableHookWSFullProps, InputTableWorkSheetLayoutProps, InputTableWSHookProps, ITHookWSFunctionResult, ITHookWSFunctionTy } from '../types'
import { memo, useCallback, useState } from 'react'
import TwinIcon from '../../../../../baseComponents/TwinIcon'
import { faBars, faChevronDown, faChevronUp, faTimes } from '@fortawesome/pro-light-svg-icons'
import TableWithCustomVirtualListing from '../../../../../baseComponents/TwinTable/TableWithCustomVirtualListing'
import useTwinTranslation from '../../../../../utils/hooks/useTwinTranslation'
import { SearchBarWithDebounce } from '../../../../../baseComponents/SearchBar'
import { ColumnITLayoutTableConfiguration } from '../../../../../baseComponents/TwinTable/InputTableLayout/InputTable/types'
import { ChangeValueFunc, Modify } from '../../../../../utils/globals/types'
import { memoChecker } from '../../../../../utils/globals/components'
import { BigTitleAndRightLayoutInputTableNewCustomer } from '../../../../../baseComponents/TwinTable/InputTableLayout'
import { InputTableLayoutData } from '../../../../../baseComponents/TwinTable/InputTableLayout/types'

interface InputTableWSLayoutProps extends InputTableWorkSheetLayoutProps{
    hookFunction: ITHookWSFunctionTy
    TableChildren: React.FC<InputTableHookWSFullProps>
}

const InputTableWorkSheetLayout: React.FC<InputTableWSLayoutProps> = ({ name, translations, children, buttonSave, columns, completeData, className, InputTableHeaderChildren, ...rest }) => {
    const { active, setActive } = useInputTableWorkSheetLayoutLogic()
    const titleWithCustomer = translations.headerTitle + ' - ' + (completeData?.header?.Customer?.fiscal_name || completeData?.header?.Customer?.name)
    return (
        <LoaderDecider loading={completeData === null}>
            <BigTitleAndRightLayout title={titleWithCustomer} className='overflow-y-auto input_table_layout' RightHeader={buttonSave}>
                <WhiteBox className={'input_table_whitebox ' + (active ? 'active' : '')}>
                    <div className='input_table_showicon' onClick={() => setActive((old) => !old)}><TwinIcon icon={active ? faTimes : faBars} /></div>
                    <div className={'input_table_header_column'}>
                        <div className='input_table_header_col_animating flex h-full'>
                            <InputTableHeaderChildren data={completeData?.header || {}} userPermissions={rest?.userPermissions} getCompleteData={rest?.getCompleteData} />
                            {children}
                        </div>
                    </div>
                    <InputTable CurrencyId={completeData?.header?.CurrencyId || ''} completeData={completeData || { header: {}, tableData: [], originalTableData: [], dictPosTableData: {}, filters: { generalSearchbar: '', order: {}, singleFieldsSearch: {} } }} {...{ name, columns, ...rest}} />
                </WhiteBox>
            </BigTitleAndRightLayout>
        </LoaderDecider>
    )
}

const useInputTableWorkSheetLayoutLogic = () => {
    const [active, setActive] = useState(true)
    return { active, setActive }
}

export const InputTableWorkSheetLayoutNew: React.FC<InputTableWSLayoutProps> = ({ completeData, ...rest }) => {
    return (
        <LoaderDecider loading={completeData === null}>
            <InputTableWorkSheetLayoutNewLoaded completeData={completeData as InputTableLayoutData} {...rest} />
        </LoaderDecider>
    )
}

type InputTableWorkSheetLayoutNewLoadedProps = Modify<InputTableWSLayoutProps, {
    completeData: InputTableLayoutData
}>

const InputTableWorkSheetLayoutNewLoaded: React.FC<InputTableWorkSheetLayoutNewLoadedProps> = ({ name, translations, buttonSave, columns, completeData, className, InputTableHeaderChildren, children, hookFunction, ...rest }) => {
    const { setSelectedColumnsAndFilters, selectedColumnsAndFilters, memorizedColumns } = useConfigColumns({ columns, name })
    const { onChangeSearchBar, ...resty } = hookFunction({ completeData, columns: memorizedColumns, CurrencyId: completeData?.header?.CurrencyId, ...rest })
    const { active, setActive } = useInputTableWorkSheetLayoutNewLogic()
    return (
        <BigTitleAndRightLayoutInputTableNewCustomer title={translations.headerTitle} className='overflow-y-auto input_table_layout' RightHeader={buttonSave} Customer={completeData?.header?.Customer}>
            <WhiteBox className='input_table_whitebox input_table_new_whitebox'>
                <div className={'input_table_top_header_parent flex relative ' + (active ? 'active' : '')}>
                    <div className='flex input_table_new_showicon cursor-pointer absolute bg-white rounded-full items-center justify-center z-3 regular16' onClick={() => setActive((old) => !old)}><TwinIcon icon={active ? faChevronUp : faChevronDown} /></div>
                    <div className='flex gap-x-15 input_table_top_header_content p-20 flex-auto'>
                        <SearchBarInputTable onChange={onChangeSearchBar} />
                        <InputTableHeaderChildren data={completeData?.header || {}} userPermissions={rest?.userPermissions} getCompleteData={rest?.getCompleteData} />
                        <ButtonConfigColumnsWithModal name={name} columns={columns} setSelectedColumnsAndFilters={setSelectedColumnsAndFilters} selectedColumnsAndFilters={selectedColumnsAndFilters} haveFiltersColumn={false} setTableConfiguration={() => { }} getTableDataFromStart={() => { }} />
                    </div>
                    {children}
                </div>
                <InputTableNew CurrencyId={completeData?.header?.CurrencyId || ''} completeData={completeData || { header: {}, tableData: [], originalTableData: [], dictPosTableData: {}, filters: { generalSearchbar: '', order: {}, singleFieldsSearch: {} } }} {...{ name, columns, className, setSelectedColumnsAndFilters, selectedColumnsAndFilters, memorizedColumns, hookFunction, onChangeSearchBar, ...rest, ...resty }} />
            </WhiteBox>
        </BigTitleAndRightLayoutInputTableNewCustomer>
    )
}

const useInputTableWorkSheetLayoutNewLogic = () => {
    const [active, setActive] = useState(true)
    return { active, setActive }
}

type InputTableProps = ComponentWithPermissions & InputTableWSHookProps &  {
    tableConfiguration: TableConfiguration
    name: string
    hookFunction: ITHookWSFunctionTy
    TableChildren: React.FC<InputTableHookWSFullProps>
    columns: ColumnITLayoutTableConfiguration[]
}


const InputTable: React.FC<InputTableProps> = ({ completeData, tableConfiguration, name, hookFunction, TableChildren, columns, ...rest }) => {
    const { setSelectedColumnsAndFilters, selectedColumnsAndFilters, memorizedColumns } = useConfigColumns({ columns, name })
    const { parsedColumns, rowRenderer, containerRenderer, onChangeSearchBar, ...resty } = hookFunction({ completeData, columns: memorizedColumns, ...rest })
    const parsedTableData = completeData?.filteredTableData || completeData.tableData
    const parseFilteredData = useCallback((rowIndex: number) => {
        return completeData?.tableData?.[completeData.dictPosTableData?.[parsedTableData?.[rowIndex].id]]
    }, [completeData.dictPosTableData, completeData.tableData, parsedTableData])
    return (
        <div className={'flex-col flex-auto flex input_table_contents'} >
                <div className='flex flex-wrap gap-10 justify-between items-center'>
                   <SearchBarInputTable onChange={onChangeSearchBar} />
                    <div>
                        <ButtonConfigColumnsWithModal name={name} columns={columns} setSelectedColumnsAndFilters={setSelectedColumnsAndFilters} selectedColumnsAndFilters={selectedColumnsAndFilters} haveFiltersColumn={false} setTableConfiguration={() => { }} getTableDataFromStart={() => { }} />
                    </div>
                </div>
            <div className='mt-30 flex flex-col flex-auto' >
                <div className={'flex flex-col flex-auto h-1 min-h-300 ' + (completeData?.tableData?.length !== parsedTableData?.length ? 'searching_input_table' : '')}>
                    <div className='flex-auto input_table_table'>
                        <TableWithCustomVirtualListing columns={parsedColumns} data={parsedTableData || []} rowRenderer={rowRenderer} containerRenderer={containerRenderer} setSelectedColumnsAndFilters={setSelectedColumnsAndFilters} name={name} updateEmployeeParameter={true} getDataFnc={parseFilteredData} />
                    </div>
                </div>
                <TableChildren {...{ completeData, columns: memorizedColumns, ...rest, ...resty }} />
            </div>
        </div>
    )
}

interface SearchBarInputTableProps {
    onChange: ChangeValueFunc
}

const SearchBarInputTable: React.FC<SearchBarInputTableProps> = memo(({ onChange }) => {
    const { t } = useTwinTranslation()
    return (
        <SearchBarWithDebounce className='search_bar_table' searchValue='' onChange={onChange} placeholder={t('search', 'Buscar')} debounce={300} />
    )
}, (oldProps, nextProps) => memoChecker(oldProps, nextProps, ['onChange']))

type InputTableNewProps = InputTableProps & ITHookWSFunctionResult & {
    selectedColumnsAndFilters: SelectedColumnsAndFilters
    setSelectedColumnsAndFilters: React.Dispatch<React.SetStateAction<SelectedColumnsAndFilters>>
    memorizedColumns: any
}

const InputTableNew: React.FC<InputTableNewProps> = ({ completeData, tableConfiguration, columns, name, TableChildren, hookFunction, memorizedColumns, setSelectedColumnsAndFilters, selectedColumnsAndFilters, parsedColumns, rowRenderer, containerRenderer, ...rest }) => {
    const parsedTableData = completeData?.filteredTableData || completeData.tableData
    const parseFilteredData = useCallback((rowIndex: number) => {
        return completeData?.tableData?.[completeData.dictPosTableData?.[parsedTableData?.[rowIndex].id]]
    }, [completeData.dictPosTableData, completeData.tableData, parsedTableData])
    return (
        <div className={'flex-col flex-auto flex input_table_contents'} >
            <div className='mt-30 flex flex-col flex-auto' >
                <div className={'flex flex-col flex-auto h-1 min-h-300 ' + (completeData?.tableData?.length !== parsedTableData?.length ? 'searching_input_table' : '')}>
                    <div className='flex-auto input_table_table'>
                        <TableWithCustomVirtualListing columns={parsedColumns} data={parsedTableData || []} rowRenderer={rowRenderer} containerRenderer={containerRenderer} setSelectedColumnsAndFilters={setSelectedColumnsAndFilters} name={name} updateEmployeeParameter={true} getDataFnc={parseFilteredData} />
                    </div>
                </div>
                <TableChildren {...{ completeData, columns: memorizedColumns, ...rest }} />
            </div>
        </div>
    )
}

export default InputTableWorkSheetLayout